/**
 * Shareable per-cartridge URLs — `?game=<id>` (sc-8150).
 *
 * The gallery was the only entry point: every inbound link landed on the
 * grid and the visitor had to find the title the link was about. That is
 * fine for "come see my arcade" and useless for "play NEON GRIND", which
 * is what a per-cartridge post actually links to.
 *
 * Two directions, one query param:
 *
 *   READ  — on boot, `pendingDeepLink()` resolves `?game=<id>` to a
 *           CatalogEntry so main.ts can launch straight into it.
 *   WRITE — while a run is mounted the id is pushed into the URL, so the
 *           address bar is always copy-pasteable; closing the game strips
 *           it again. History is REPLACED, never pushed: a run is not a
 *           navigation step, and pushing would turn Back into "reopen the
 *           cartridge you just closed".
 *
 * Resolution is against `CATALOG`, which is already production-filtered
 * (catalog.ts:326) — so a `stage: 'dev'` cartridge is unreachable by URL
 * on a production build for free, matching the gallery. An unknown or
 * dev-only id falls through to the plain gallery rather than erroring;
 * a stale link should look like the front door, not a 404.
 *
 * The param name is deliberately NOT one of the auth/billing params
 * (`code`, `state`, `token`, PayPal's) — callback.ts and return.ts run
 * first on boot and own those. `game` is unclaimed by both.
 */

import { CATALOG, type CatalogEntry } from '../catalog.js';

/** The one query param the cabinet routes on. */
export const GAME_PARAM = 'game';

/**
 * Resolve `?game=<id>` to a catalog entry, or null when absent/unknown.
 *
 * Unknown ids resolve to null on purpose: a link to a retired or
 * dev-gated cartridge lands on the gallery instead of an error state.
 */
export function pendingDeepLink(): CatalogEntry | null {
  const id = new URLSearchParams(window.location.search).get(GAME_PARAM);
  if (!id) return null;
  return CATALOG.find((entry) => entry.id === id) ?? null;
}

/**
 * Reflect the running cartridge in the address bar so the URL a player
 * copies mid-run is the URL that reopens that cartridge.
 *
 * replaceState, not pushState — see the header note on Back.
 */
export function markDeepLink(id: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get(GAME_PARAM) === id) return;
  url.searchParams.set(GAME_PARAM, id);
  window.history.replaceState(null, '', url);
}

/**
 * Drop `?game=` when the player returns to the gallery, so a reload (or a
 * later copy of the URL) shows the arcade rather than silently
 * relaunching the cartridge they just quit.
 */
export function clearDeepLink(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(GAME_PARAM)) return;
  url.searchParams.delete(GAME_PARAM);
  // Preserve a bare '?' -> drop it entirely for a clean shareable root.
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}
